import dayjs from "dayjs";
import {v4 as uuid} from "uuid";

import {isApp} from "@atg-shared/system";
import {
    GameType,
    type AdvancedOutcomes,
    type BetCouponV2,
    type OfferingsAndReceiptsResponse,
    type OfferingsAndReceiptsV2Response,
    type Receipt,
    type ReceiptV2,
} from "@atg-sport-shared/pool-types/betTypes";
import {
    AdvancedSelection,
    SimpleSelection,
    CouponType,
} from "@atg-sport-shared/pool-types/couponTypes";
import {
    OfferingStatus,
    type Offering,
    type OfferingWithMatchRefs,
} from "@atg-sport-shared/pool-types/offeringTypes";
import {
    AdvancedBetSelection,
    BetSelection,
} from "@atg-sport-shared/pool-types/selectionTypes";

import type {AtgResponse} from "@atg-shared/fetch-types";
import type {FullTimeOutcome, OverUnderOutcome} from "@atg-sport-shared/pool-types";
import type {
    Coupon,
    MatchNumberAndId,
    Selection,
    SP5Selection,
    SP5Coupon,
    Big9Coupon,
    Big9Selection,
    ApiCoupon,
} from "@atg-sport-shared/pool-types/couponTypes";
import type {MatchNumber, MatchRef} from "@atg-sport-shared/pool-types/matchTypes";

export const DEFAULT_ROW_COST: Record<GameType, number> = {
    [GameType.Big9]: 50,
    [GameType.Sp5]: 100,
    [GameType.Sp96]: 50,
};

const {SIMPLE, ADVANCED} = CouponType;

export const emptySimpleRow = {
    [SimpleSelection.HOME]: false,
    [SimpleSelection.TIE]: false,
    [SimpleSelection.AWAY]: false,
    [SimpleSelection.OVER]: false,
    [SimpleSelection.UNDER]: false,
};

export const emptyAdvancedRow = {
    [AdvancedSelection.HOME_OVER]: false,
    [AdvancedSelection.HOME_UNDER]: false,
    [AdvancedSelection.TIE_OVER]: false,
    [AdvancedSelection.TIE_UNDER]: false,
    [AdvancedSelection.AWAY_OVER]: false,
    [AdvancedSelection.AWAY_UNDER]: false,
};

/**
 * Type guard for pool types
 */
export const isSP5Coupon = (coupon: Coupon): coupon is SP5Coupon =>
    coupon.gameType === GameType.Sp5;

export const isBig9Coupon = (coupon: Coupon): coupon is Big9Coupon =>
    coupon.gameType === GameType.Big9 || coupon.gameType === GameType.Sp96;

export const isLegacyBig9Coupon = (coupon: Coupon): coupon is Big9Coupon =>
    coupon.gameType === GameType.Big9;

export const requiresWildcardForGameType = (gameType?: GameType): boolean =>
    gameType === GameType.Big9;

export const requiresWildcardForCoupon = (coupon: Coupon): boolean =>
    requiresWildcardForGameType(coupon.gameType);

export const calculateSimpleRowCount = (coupon: Coupon) => {
    let rowCount = 1;

    Object.values(coupon.coupon.selections).forEach((selection) => {
        if (requiresWildcardForCoupon(coupon)) {
            if (coupon.coupon.wildcard === selection.matchNumber) return;
        }

        let homeTieAwaySelectionCount = 0;
        if (selection.simple.home) homeTieAwaySelectionCount += 1;
        if (selection.simple.tie) homeTieAwaySelectionCount += 1;
        if (selection.simple.away) homeTieAwaySelectionCount += 1;
        if (homeTieAwaySelectionCount === 0) homeTieAwaySelectionCount = 1;

        let overUnderSelectionCount = 0;
        if (selection.simple.over) overUnderSelectionCount += 1;
        if (selection.simple.under) overUnderSelectionCount += 1;
        if (overUnderSelectionCount === 0) overUnderSelectionCount = 1;

        rowCount *= homeTieAwaySelectionCount * overUnderSelectionCount;
    });

    return rowCount;
};

export const calculateAdvancedRowCount = (coupon: Coupon) => {
    let rowCount = 1;

    Object.values(coupon.coupon.selections).forEach((selection) => {
        if (requiresWildcardForCoupon(coupon)) {
            if (coupon.coupon.wildcard === selection.matchNumber) return;
        }

        let selectionCount = 0;

        if (selection.advanced.homeOver) selectionCount += 1;
        if (selection.advanced.homeUnder) selectionCount += 1;
        if (selection.advanced.tieOver) selectionCount += 1;
        if (selection.advanced.tieUnder) selectionCount += 1;
        if (selection.advanced.awayOver) selectionCount += 1;
        if (selection.advanced.awayUnder) selectionCount += 1;

        if (selectionCount === 0) selectionCount = 1;

        rowCount *= selectionCount;
    });

    return rowCount;
};

export const calculateRowCount = (coupon: Coupon) => {
    if (coupon.coupon.type === CouponType.SIMPLE) {
        return calculateSimpleRowCount(coupon);
    }
    if (coupon.coupon.type === CouponType.ADVANCED) {
        return calculateAdvancedRowCount(coupon);
    }
    throw Error("No such coupon type");
};

export const generateDefaultCoupon = (
    offeringId: string,
    matches: ReadonlyArray<Pick<MatchRef, "id" | "number">>,
    startTime: string,
    gameType: GameType | undefined,
): Coupon => {
    const modified = new Date().toISOString();
    const couponId = uuid();

    if (!gameType) {
        throw new Error("Game type is undefined. Cannot generate default coupon.");
    }

    if (gameType === GameType.Sp5) {
        return {
            id: couponId,
            gameId: offeringId,
            gameType: GameType.Sp5,
            gameDate: startTime,
            modified,
            coupon: {
                version: 2,
                rowCost: DEFAULT_ROW_COST[GameType.Sp5],
                type: SIMPLE,
                wildcard: null,
                harryStake: 0,
                selections: matches.reduce((acc, {id: matchId, number: matchNumber}) => {
                    acc[matchNumber] = {
                        flexHarryOpen: false,
                        harryIsDisabled: true,
                        matchId,
                        matchNumber,
                        simple: emptySimpleRow,
                        advanced: emptyAdvancedRow,
                    };

                    return acc;
                }, {} as {[matchNumber in MatchNumber]: SP5Selection}),
            },
        };
    }

    return {
        id: couponId,
        gameId: offeringId,
        gameType,
        gameDate: startTime,
        modified,
        coupon: {
            version: 2,
            rowCost: DEFAULT_ROW_COST[gameType],
            wildcard: null,
            harryStake: 0,
            type: SIMPLE,
            selections: matches.reduce((acc, {id: matchId, number: matchNumber}) => {
                acc[matchNumber] = {
                    matchId,
                    matchNumber,
                    flexHarryOpen: true,
                    harryIsDisabled: true,
                    simple: emptySimpleRow,
                    advanced: emptyAdvancedRow,
                };

                return acc;
            }, {} as {[matchNumber in MatchNumber]: Big9Selection}),
        },
    };
};

/**
 * Type guard for advanced selections
 */
export const isAdvancedSelections = (
    selection: Selection[typeof ADVANCED] | Selection[typeof SIMPLE],
): selection is Selection[typeof ADVANCED] =>
    (selection as Selection[typeof ADVANCED]).homeOver !== undefined;

/**
 * Type guard for simple selections
 */
export const isSimpleSelections = (
    selection: Selection[typeof ADVANCED] | Selection[typeof SIMPLE],
): selection is Selection[typeof SIMPLE] =>
    (selection as Selection[typeof SIMPLE]).home !== undefined;

export const isAdvancedSelection = (selection: SimpleSelection | AdvancedSelection) =>
    Object.values(AdvancedSelection).includes(selection as AdvancedSelection);

const mapSelections = (
    outcomeType: BetSelection | AdvancedBetSelection,
    matchNumber: MatchNumber,
    coupon: BetCouponV2,
    outcome1: AdvancedOutcomes,
    outcome2?: AdvancedOutcomes,
    outcome3?: AdvancedOutcomes,
): {[a: string]: boolean} | undefined => {
    const match = coupon.selections.legs[matchNumber];

    if (
        match.includes(outcome1) ||
        (outcome2 && match.includes(outcome2)) ||
        (outcome3 && match.includes(outcome3))
    ) {
        return {[outcomeType]: true};
    }
    return undefined;
};

const isMatchCanceled = (
    matchNumber: MatchNumber,
    canceledLegs?: ReadonlyArray<MatchNumber>,
) =>
    canceledLegs && canceledLegs.find((nr) => nr === matchNumber)
        ? {canceled: true}
        : false;

type ReceiptConversionOptions = {
    isHarryBet?: boolean;
    isFlexHarry?: boolean;
    maxCost?: number;
};

export const convertNewReceiptToOld = (
    receipt: ReceiptV2,
    offering: Offering,
    options?: ReceiptConversionOptions,
) => {
    const summary = options?.isHarryBet
        ? {
              harry: true,
              flex: !!options.isFlexHarry,
              filebet: false,
              shop: false,
              tillsammans: false,
              couponTeam: false,
          }
        : receipt.summary;

    const result = {
        ...receipt,
        summary,
        bets: receipt.bets.map((bet) => {
            const fees =
                options?.isHarryBet && bet.fees === 0 && options.maxCost
                    ? options.maxCost - bet.stake
                    : bet.fees;

            return {
                ...bet,
                fees,
                coupons: bet.coupons.map((coupon) => ({
                    stakePerCombination: coupon.stakePerCombination,
                    totalStake: coupon.stake,
                    matches: offering.matches.map(({id, number}) => ({
                        id,
                        number,
                        advancedSelections: isMatchCanceled(number, bet.canceledLegs) || {
                            ...mapSelections(
                                AdvancedBetSelection.HomeOver,
                                number,
                                coupon,
                                "1/O",
                            ),
                            ...mapSelections(
                                AdvancedBetSelection.HomeUnder,
                                number,
                                coupon,
                                "1/U",
                            ),
                            ...mapSelections(
                                AdvancedBetSelection.TieOver,
                                number,
                                coupon,
                                "X/O",
                            ),
                            ...mapSelections(
                                AdvancedBetSelection.TieUnder,
                                number,
                                coupon,
                                "X/U",
                            ),
                            ...mapSelections(
                                AdvancedBetSelection.AwayOver,
                                number,
                                coupon,
                                "2/O",
                            ),
                            ...mapSelections(
                                AdvancedBetSelection.AwayUnder,
                                number,
                                coupon,
                                "2/U",
                            ),
                            ...mapSelections(
                                AdvancedBetSelection.Wildcard,
                                number,
                                coupon,
                                "*",
                            ),
                        },
                        selections: isMatchCanceled(number, bet.canceledLegs) || {
                            ...mapSelections(
                                BetSelection.Home,
                                number,
                                coupon,
                                "1/O",
                                "1/U",
                            ),
                            ...mapSelections(
                                BetSelection.Tie,
                                number,
                                coupon,
                                "X/O",
                                "X/U",
                            ),
                            ...mapSelections(
                                BetSelection.Away,
                                number,
                                coupon,
                                "2/O",
                                "2/U",
                            ),
                            ...mapSelections(
                                BetSelection.Over,
                                number,
                                coupon,
                                "1/O",
                                "X/O",
                                "2/O",
                            ),
                            ...mapSelections(
                                BetSelection.Under,
                                number,
                                coupon,
                                "1/U",
                                "X/U",
                                "2/U",
                            ),
                            ...mapSelections(BetSelection.Wildcard, number, coupon, "*"),
                        },
                    })),
                    mode: coupon.selections.mode,
                })),
            };
        }),
    };
    return result;
};

export const convertNewReceiptsResponseToOld = (
    res: AtgResponse<OfferingsAndReceiptsV2Response>,
    options?: ReceiptConversionOptions,
): AtgResponse<OfferingsAndReceiptsResponse> => {
    const {offerings} = res.data;

    const receipts: ReadonlyArray<Receipt> = res.data.receipts.map((receipt) => {
        const offering = offerings[receipt.offeringId];
        return convertNewReceiptToOld(receipt, offering, options);
    });

    return {...res, data: {offerings: res.data.offerings, receipts}};
};

export const getMatchNumbersAndIds = (coupon: Coupon): ReadonlyArray<MatchNumberAndId> =>
    Object.values(coupon.coupon.selections)
        .map((selection) => ({
            number: selection.matchNumber,
            id: selection.matchId,
        }))
        .sort((a, b) => parseInt(a.number, 10) - parseInt(b.number, 10));

export const showCoupon = (offering: OfferingWithMatchRefs | null) => {
    const isOfferingCancelled = offering?.status === OfferingStatus.CANCELED;
    const hasOfferingStarted = dayjs().diff(offering?.startTime) > 0;

    return (
        offering?.status === OfferingStatus.SELL_OPEN ||
        offering?.status === OfferingStatus.UNDER_REVIEW ||
        (!hasOfferingStarted && isOfferingCancelled)
    );
};

// App usage

const getFullTimeSelections = (selection: Selection): ReadonlyArray<FullTimeOutcome> => {
    const outcomes: FullTimeOutcome[] = [];
    if (selection.simple.home) outcomes.push(BetSelection.Home);
    if (selection.simple.tie) outcomes.push(BetSelection.Tie);
    if (selection.simple.away) outcomes.push(BetSelection.Away);
    return outcomes;
};

const getOverUnderSelections = (
    selection: Selection,
): ReadonlyArray<OverUnderOutcome> => {
    const outcomes: OverUnderOutcome[] = [];
    if (selection.simple.over) outcomes.push(BetSelection.Over);
    if (selection.simple.under) outcomes.push(BetSelection.Under);
    return outcomes;
};

export const convertApiCouponToPoolCoupon = (
    coupon: ApiCoupon,
    matchRefs: readonly MatchNumberAndId[],
): Coupon => {
    const matchIdToNumberMap = matchRefs.reduce((acc, matchRef) => {
        acc[matchRef.id] = matchRef.number;
        return acc;
    }, {} as {[matchId: string]: MatchNumber});

    const convertOldWildcardToNew = () => {
        if (!coupon.coupon.wildcard) return null;
        const wildcardMatchId = coupon.coupon.wildcard.split(`${coupon.id}-`)[1];
        return matchIdToNumberMap[wildcardMatchId];
    };

    return {
        ...coupon,
        coupon: {
            version: 2,
            type: coupon.coupon.type || SIMPLE,
            status: coupon.coupon.status,
            rowCost: coupon.coupon.rowCost,
            wildcard: convertOldWildcardToNew(),
            harryStake: coupon.coupon.harryStake,
            selections: Object.keys(coupon.coupon.outcomes).reduce((acc, key) => {
                const {matchId} = coupon.coupon.outcomes[key];
                const matchNumber = matchIdToNumberMap[matchId];

                const simpleFallback = {
                    home: coupon.coupon.outcomes[key].fullTime.includes(
                        BetSelection.Home,
                    ),
                    tie: coupon.coupon.outcomes[key].fullTime.includes(BetSelection.Tie),
                    away: coupon.coupon.outcomes[key].fullTime.includes(
                        BetSelection.Away,
                    ),
                    over: coupon.coupon.outcomes[key].overUnder.includes(
                        BetSelection.Over,
                    ),
                    under: coupon.coupon.outcomes[key].overUnder.includes(
                        BetSelection.Under,
                    ),
                };
                const advancedFallback = {
                    homeOver:
                        coupon.coupon.outcomes[key].fullTime.includes(
                            BetSelection.Home,
                        ) &&
                        coupon.coupon.outcomes[key].overUnder.includes(BetSelection.Over),
                    homeUnder:
                        coupon.coupon.outcomes[key].fullTime.includes(
                            BetSelection.Home,
                        ) &&
                        coupon.coupon.outcomes[key].overUnder.includes(
                            BetSelection.Under,
                        ),
                    tieOver:
                        coupon.coupon.outcomes[key].fullTime.includes(BetSelection.Tie) &&
                        coupon.coupon.outcomes[key].overUnder.includes(BetSelection.Over),
                    tieUnder:
                        coupon.coupon.outcomes[key].fullTime.includes(BetSelection.Tie) &&
                        coupon.coupon.outcomes[key].overUnder.includes(
                            BetSelection.Under,
                        ),
                    awayOver:
                        coupon.coupon.outcomes[key].fullTime.includes(
                            BetSelection.Away,
                        ) &&
                        coupon.coupon.outcomes[key].overUnder.includes(BetSelection.Over),
                    awayUnder:
                        coupon.coupon.outcomes[key].fullTime.includes(
                            BetSelection.Away,
                        ) &&
                        coupon.coupon.outcomes[key].overUnder.includes(
                            BetSelection.Under,
                        ),
                };

                return {
                    ...acc,
                    [matchNumber]: {
                        matchId,
                        matchNumber,
                        flexHarryOpen:
                            coupon.gameType === GameType.Sp5
                                ? false
                                : coupon.coupon.outcomes[key].harry,
                        harryIsDisabled:
                            coupon.gameType === GameType.Sp5
                                ? true
                                : coupon.coupon.outcomes[key].harryIsDisabled,
                        // @ts-expect-error
                        simple: coupon.coupon.selections
                            ? // @ts-expect-error
                              coupon.coupon.selections[matchNumber].simple
                            : simpleFallback,
                        // @ts-expect-error
                        advanced: coupon.coupon.selections
                            ? // @ts-expect-error
                              coupon.coupon.selections[matchNumber].advanced
                            : advancedFallback,
                    },
                };
            }, {} as Coupon["coupon"]["selections"]),
        },
    };
};

export const isOldCoupon = (coupon: Coupon | ApiCoupon): coupon is ApiCoupon =>
    coupon.coupon.version === 1;

export const convertPoolCouponToApiCoupon = (coupon: Coupon | ApiCoupon): ApiCoupon => {
    if (isOldCoupon(coupon)) return coupon;

    const matchNumberToIdMap = Object.values(coupon.coupon.selections).reduce(
        (acc, selection) => {
            acc[selection.matchNumber] = `${coupon.id}-${selection.matchId}`;
            return acc;
        },
        {} as {[matchNumber in MatchNumber]: string},
    );

    const appRuntimeSpecific = isApp
        ? {
              totalCost: coupon.coupon.rowCost * calculateRowCount(coupon),
              selections: coupon.coupon.selections,
          }
        : {};

    return {
        ...coupon,
        isPublic: false,
        coupon: {
            version: 1,
            status: coupon.coupon.status,
            rowCost: coupon.coupon.rowCost,
            wildcard: !coupon.coupon.wildcard
                ? null
                : matchNumberToIdMap[coupon.coupon.wildcard],
            harryStake: coupon.coupon.harryStake,
            outcomes: Object.keys(coupon.coupon.selections).reduce<
                ApiCoupon["coupon"]["outcomes"]
            >((acc, key) => {
                const selections = coupon.coupon.selections[key as MatchNumber];
                const outcomeId = `${coupon.id}-${selections.matchId}`;
                return {
                    ...acc,
                    [outcomeId]: {
                        fullTime: getFullTimeSelections(selections),
                        overUnder: getOverUnderSelections(selections),
                        harry: selections.flexHarryOpen,
                        matchId: selections.matchId,
                        harryIsDisabled: selections.harryIsDisabled, // TODO---> Remove this
                    },
                };
            }, {}),
            type: coupon.coupon.type,
            ...appRuntimeSpecific,
        },
    };
};

export const convertOldCouponToNew = (
    coupon: ApiCoupon,
    matchRefs: readonly MatchNumberAndId[],
): Coupon => {
    const matchIdToNumberMap = matchRefs.reduce((acc, matchRef) => {
        acc[matchRef.id] = matchRef.number;
        return acc;
    }, {} as {[matchId: string]: MatchNumber});

    const convertOldWildcardToNew = () => {
        if (!coupon.coupon.wildcard) return null;
        const wildcardMatchId = coupon.coupon.wildcard.split(`${coupon.id}-`)[1];
        return matchIdToNumberMap[wildcardMatchId];
    };

    return {
        ...coupon,
        coupon: {
            version: 2,
            type: coupon.coupon.type || SIMPLE,
            status: coupon.coupon.status,
            rowCost: coupon.coupon.rowCost,
            wildcard: convertOldWildcardToNew(),
            harryStake: coupon.coupon.harryStake,
            selections: Object.keys(coupon.coupon.outcomes).reduce((acc, key) => {
                const {matchId} = coupon.coupon.outcomes[key];
                const matchNumber = matchIdToNumberMap[matchId];

                const simpleFallback = {
                    home: coupon.coupon.outcomes[key].fullTime.includes(
                        BetSelection.Home,
                    ),
                    tie: coupon.coupon.outcomes[key].fullTime.includes(BetSelection.Tie),
                    away: coupon.coupon.outcomes[key].fullTime.includes(
                        BetSelection.Away,
                    ),
                    over: coupon.coupon.outcomes[key].overUnder.includes(
                        BetSelection.Over,
                    ),
                    under: coupon.coupon.outcomes[key].overUnder.includes(
                        BetSelection.Under,
                    ),
                };
                const advancedFallback = {
                    homeOver:
                        coupon.coupon.outcomes[key].fullTime.includes(
                            BetSelection.Home,
                        ) &&
                        coupon.coupon.outcomes[key].overUnder.includes(BetSelection.Over),
                    homeUnder:
                        coupon.coupon.outcomes[key].fullTime.includes(
                            BetSelection.Home,
                        ) &&
                        coupon.coupon.outcomes[key].overUnder.includes(
                            BetSelection.Under,
                        ),
                    tieOver:
                        coupon.coupon.outcomes[key].fullTime.includes(BetSelection.Tie) &&
                        coupon.coupon.outcomes[key].overUnder.includes(BetSelection.Over),
                    tieUnder:
                        coupon.coupon.outcomes[key].fullTime.includes(BetSelection.Tie) &&
                        coupon.coupon.outcomes[key].overUnder.includes(
                            BetSelection.Under,
                        ),
                    awayOver:
                        coupon.coupon.outcomes[key].fullTime.includes(
                            BetSelection.Away,
                        ) &&
                        coupon.coupon.outcomes[key].overUnder.includes(BetSelection.Over),
                    awayUnder:
                        coupon.coupon.outcomes[key].fullTime.includes(
                            BetSelection.Away,
                        ) &&
                        coupon.coupon.outcomes[key].overUnder.includes(
                            BetSelection.Under,
                        ),
                };

                return {
                    ...acc,
                    [matchNumber]: {
                        matchId,
                        matchNumber,
                        flexHarryOpen: coupon.coupon.outcomes[key].harry,
                        harryIsDisabled: coupon.coupon.outcomes[key].harryIsDisabled,
                        // @ts-expect-error
                        simple: coupon.coupon.selections
                            ? // @ts-expect-error
                              coupon.coupon.selections[matchNumber].simple
                            : simpleFallback,
                        // @ts-expect-error
                        advanced: coupon.coupon.selections
                            ? // @ts-expect-error
                              coupon.coupon.selections[matchNumber].advanced
                            : advancedFallback,
                    },
                };
            }, {} as Coupon["coupon"]["selections"]),
        },
    };
};

export const convertToNewFormat = (
    coupon: Coupon | ApiCoupon,
    matchRefs: readonly MatchNumberAndId[],
): Coupon => {
    if (isOldCoupon(coupon)) {
        return convertOldCouponToNew(coupon, matchRefs);
    }
    return coupon;
};
